export const DEFERRED_FORM_RESOURCES_ID = 'kf-deferred-form-resources';

const TRIGGER_SELECTOR = '.EPiServerForms, [data-open-form-id]';
const RECAPTCHA_READY_TIMEOUT_MS = 5000;

export interface DeferredFormResources {
  scripts: string[];
  css: string[];
}

type OnBeforeSubmit = (this: EpiFormsExtension, form: HTMLElement) => unknown;

interface EpiFormsExtension {
  onBeforeSubmit?: OnBeforeSubmit;
}

interface JQueryDeferredLike {
  resolve: (value?: unknown) => void;
  reject: (reason?: unknown) => void;
  promise: () => unknown;
}

interface EpiFormsJQuery {
  (selector: unknown): unknown;
  Deferred: () => JQueryDeferredLike;
  when: (value: unknown) => { then: (done: (value?: unknown) => void, fail: (reason?: unknown) => void) => void };
}

export type DeferredFormsWindow = Window & {
  $$epiforms?: unknown;
  epi?: { EPiServer?: { Forms?: { Extension?: EpiFormsExtension } } };
  grecaptcha?: { ready?: (callback: () => void) => void };
};

const isStringArray = (value: unknown): value is string[] => Array.isArray(value) && value.every((item) => typeof item === 'string');

export type DeferredFormResourceKind = 'script' | 'css';

const UTIL_PATH_PREFIX = '/Util/';

// Third-party loaders emitted by EPiServer.Forms.Samples 4.2.7: reCAPTCHA, hCaptcha and the Addresses element.
const ALLOWED_EXTERNAL_SCRIPTS: ReadonlyArray<{ host: string; paths: readonly string[] }> = [
  { host: 'www.google.com', paths: ['/recaptcha/api.js', '/recaptcha/enterprise.js'] },
  { host: 'js.hcaptcha.com', paths: ['/1/api.js'] },
  { host: 'maps.google.com', paths: ['/maps/api/js'] },
];

// The URL parser silently strips tab/CR/LF and treats `\` as `/`, so `/\t/evil.tld/x.js` would become `//evil.tld/x.js`.
const hasAmbiguousUrlChars = (raw: string) => /[\u0000-\u0020\u007f\\]/.test(raw);

const isAllowedExternalScript = (url: URL) =>
  url.protocol === 'https:' &&
  url.port === '' &&
  ALLOWED_EXTERNAL_SCRIPTS.some(({ host, paths }) => url.hostname === host && paths.includes(url.pathname));

/**
 * Returns the normalised href to inject, or undefined when the URL is not a same-origin `/Util/` resource
 * (or, for scripts, an allowlisted third-party loader). Callers must inject the returned href, not the raw value.
 */
export const resolveDeferredFormResourceUrl = (
  raw: string,
  kind: DeferredFormResourceKind,
  baseUrl: string = document.baseURI
): string | undefined => {
  if (!raw || hasAmbiguousUrlChars(raw)) return undefined;

  let url: URL;
  let base: URL;

  try {
    base = new URL(baseUrl);
    url = new URL(raw, base);
  } catch {
    return undefined;
  }

  if (url.protocol !== 'https:' && url.protocol !== 'http:') return undefined;

  if (url.origin === base.origin) {
    return url.pathname.startsWith(UTIL_PATH_PREFIX) ? url.href : undefined;
  }

  return kind === 'script' && isAllowedExternalScript(url) ? url.href : undefined;
};

const resolveAllowedUrls = (urls: string[], kind: DeferredFormResourceKind, baseUrl: string) =>
  urls.flatMap((raw) => {
    const href = resolveDeferredFormResourceUrl(raw, kind, baseUrl);

    if (href === undefined) {
      console.warn(`[deferred-form-resources] Dropped disallowed ${kind} URL:`, raw);

      return [];
    }

    return [href];
  });

export const readDeferredFormResources = (doc: Document = document): DeferredFormResources | undefined => {
  const element = doc.getElementById(DEFERRED_FORM_RESOURCES_ID);

  if (!(element instanceof HTMLScriptElement) || element.type !== 'application/json' || !element.textContent) return undefined;

  try {
    const parsed = JSON.parse(element.textContent) as Partial<DeferredFormResources>;
    const scripts = isStringArray(parsed.scripts) ? resolveAllowedUrls(parsed.scripts, 'script', doc.baseURI) : [];
    const css = isStringArray(parsed.css) ? resolveAllowedUrls(parsed.css, 'css', doc.baseURI) : [];

    return scripts.length || css.length ? { scripts, css } : undefined;
  } catch {
    return undefined;
  }
};

// Sink-side guard: the value assigned to `href` / `src` is always the one that passed the allowlist.
const appendStylesheet = (doc: Document, raw: string) => {
  const href = resolveDeferredFormResourceUrl(raw, 'css', doc.baseURI);

  if (href === undefined) return;

  const link = doc.createElement('link');

  link.rel = 'stylesheet';
  link.type = 'text/css';
  link.href = href;
  doc.head.appendChild(link);
};

// Resolves on error too: a blocked script (e.g. reCAPTCHA behind an ad blocker) must not stall form submission.
const appendScript = (doc: Document, raw: string) =>
  new Promise<void>((resolve) => {
    const src = resolveDeferredFormResourceUrl(raw, 'script', doc.baseURI);

    if (src === undefined) {
      resolve();

      return;
    }

    const script = doc.createElement('script');

    script.type = 'text/javascript';
    // Dynamically inserted scripts are async by default; Samples depends on jQuery UI executing first.
    script.async = false;
    script.src = src;
    script.addEventListener('load', () => resolve());
    script.addEventListener('error', () => resolve());
    doc.body.appendChild(script);
  });

const waitForRecaptcha = (win: DeferredFormsWindow, timeoutMs: number) =>
  new Promise<void>((resolve) => {
    const ready = win.grecaptcha?.ready;

    if (typeof ready !== 'function') {
      resolve();

      return;
    }

    const timer = win.setTimeout(resolve, timeoutMs);

    ready(() => {
      win.clearTimeout(timer);
      resolve();
    });
  });

export const createDeferredFormResourceLoader = (
  resources: DeferredFormResources,
  options: {
    doc?: Document;
    win?: DeferredFormsWindow;
    onScriptsLoaded?: () => void;
    recaptchaTimeoutMs?: number;
  } = {}
) => {
  const { doc = document, win = window as DeferredFormsWindow, onScriptsLoaded, recaptchaTimeoutMs = RECAPTCHA_READY_TIMEOUT_MS } = options;
  let loading: Promise<void> | undefined;

  return (): Promise<void> => {
    if (!loading) {
      resources.css.forEach((href) => appendStylesheet(doc, href));

      loading = Promise.all(resources.scripts.map((src) => appendScript(doc, src))).then(() => {
        onScriptsLoaded?.();

        return waitForRecaptcha(win, recaptchaTimeoutMs);
      });
    }

    return loading;
  };
};

export const bindInteractionTriggers = (load: () => Promise<void>, doc: Document = document, win: Window = window) => {
  const onInteraction = (event: Event) => {
    const target = event.target;

    if (target instanceof Element && target.closest(TRIGGER_SELECTOR)) {
      trigger();
    }
  };

  const trigger = () => {
    doc.removeEventListener('focusin', onInteraction, true);
    doc.removeEventListener('pointerdown', onInteraction, true);
    win.removeEventListener('openForm', trigger);
    load();
  };

  doc.addEventListener('focusin', onInteraction, true);
  doc.addEventListener('pointerdown', onInteraction, true);
  win.addEventListener('openForm', trigger);
};

/**
 * Makes Forms submit / next-step wait for the deferred scripts. Forms Samples replaces
 * `Extension.onBeforeSubmit` (to fetch the reCAPTCHA token) when it loads, so the returned
 * function re-wraps that replacement.
 */
export const installSubmitGuard = (load: () => Promise<void>, win: DeferredFormsWindow = window as DeferredFormsWindow) => {
  const $ = win.$$epiforms as EpiFormsJQuery | undefined;
  const extension = win.epi?.EPiServer?.Forms?.Extension;

  if (typeof $ !== 'function' || !extension || typeof extension.onBeforeSubmit !== 'function') {
    return () => {};
  }

  let inner: OnBeforeSubmit = extension.onBeforeSubmit;

  const reclaim = () => {
    if (typeof extension.onBeforeSubmit === 'function' && extension.onBeforeSubmit !== guarded) {
      inner = extension.onBeforeSubmit;
      extension.onBeforeSubmit = guarded;
    }
  };

  const guarded: OnBeforeSubmit = function (form) {
    const deferred = $.Deferred();

    load().then(() => {
      reclaim();
      $.when(inner.call(extension, form)).then(deferred.resolve, deferred.reject);
    });

    return deferred.promise();
  };

  extension.onBeforeSubmit = guarded;

  return reclaim;
};

export const initDeferredFormResources = (doc: Document = document, win: DeferredFormsWindow = window as DeferredFormsWindow) => {
  const resources = readDeferredFormResources(doc);

  if (!resources) return;

  let reclaimSubmitGuard = () => {};
  const load = createDeferredFormResourceLoader(resources, { doc, win, onScriptsLoaded: () => reclaimSubmitGuard() });

  reclaimSubmitGuard = installSubmitGuard(load, win);
  bindInteractionTriggers(load, doc, win);
};
